import { useState, useEffect, useRef } from "react";
import { z } from "zod";
import {
  CsvImportType,
  CsvImportSummary,
  CsvImportResponseSchema,
  AssetSchema,
  CSV_IMPORT_STATUS,
} from "@forgepoint/shared";
import { fetchApi, apiUrl, getAuthToken } from "@/lib/api";

export type ImportStage =
  | "idle"
  | "uploading"
  | "processing"
  | "complete"
  | "failed";

const PresignedUrlResponseSchema = z.object({
  uploadUrl: z.string(),
  asset: AssetSchema,
});

const ImportApiResponseSchema = z.object({
  import: CsvImportResponseSchema,
});

export interface UseCsvImportResult {
  stage: ImportStage;
  summary: CsvImportSummary | null;
  errorMessage: string | null;
  startImport: (file: File) => Promise<void>;
  downloadTemplate: () => Promise<void>;
  reset: () => void;
}

/**
 * Hook that manages the full lifecycle of a CSV import:
 * presigned URL request, S3 upload, import record creation, and status polling.
 * @param importType - The type of entity being imported
 * @returns Import state and control functions
 */
export function useCsvImport(importType: CsvImportType): UseCsvImportResult {
  const [stage, setStage] = useState<ImportStage>("idle");
  const [summary, setSummary] = useState<CsvImportSummary | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current !== null) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const stopPolling = () => {
    if (pollingIntervalRef.current !== null) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const startPolling = (importId: string) => {
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await fetchApi(`imports/${importId}`, "GET");

        if (!response.ok) {
          stopPolling();
          setStage("failed");
          setErrorMessage("Failed to check import status.");
          return;
        }

        const parsed = ImportApiResponseSchema.safeParse(response.data);
        if (!parsed.success) {
          stopPolling();
          setStage("failed");
          setErrorMessage("Unexpected response from server.");
          return;
        }

        const importRecord = parsed.data.import;

        if (importRecord.status === CSV_IMPORT_STATUS.COMPLETE) {
          stopPolling();
          setSummary(importRecord.summary);
          setStage("complete");
        } else if (importRecord.status === CSV_IMPORT_STATUS.FAILED) {
          stopPolling();
          setErrorMessage(importRecord.errorMessage ?? "Import failed.");
          setStage("failed");
        }
      } catch {
        stopPolling();
        setStage("failed");
        setErrorMessage("Failed to check import status.");
      }
    }, 2000);
  };

  /**
   * Uploads a CSV file to S3 and kicks off the async import job.
   * @param file - The CSV file to import
   */
  const startImport = async (file: File) => {
    setStage("uploading");
    setErrorMessage(null);
    setSummary(null);

    try {
      const uploadUrlResponse = await fetchApi("api/assets/upload", "POST", {
        body: {
          fileName: file.name,
          fileType: file.type || "text/csv",
          fileSize: file.size,
        },
      });

      if (!uploadUrlResponse.ok) {
        throw new Error("Failed to get upload URL.");
      }

      const presignedData = PresignedUrlResponseSchema.parse(
        uploadUrlResponse.data,
      );

      const s3Response = await fetch(presignedData.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "text/csv" },
      });

      if (!s3Response.ok) {
        throw new Error("Failed to upload file.");
      }

      const importResponse = await fetchApi("imports", "POST", {
        body: {
          importType,
          assetId: presignedData.asset.id,
          fileName: file.name,
        },
      });

      if (!importResponse.ok) {
        throw new Error("Failed to start import.");
      }

      const importData = ImportApiResponseSchema.parse(importResponse.data);
      setStage("processing");
      startPolling(importData.import.id);
    } catch (error) {
      setStage("failed");
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "An unexpected error occurred.",
      );
    }
  };

  /**
   * Fetches the CSV template for the import type and triggers a browser download.
   */
  const downloadTemplate = async () => {
    const authToken = getAuthToken();
    const headers: Record<string, string> = authToken
      ? { Authorization: `Bearer ${authToken}` }
      : {};

    const response = await fetch(`${apiUrl}/imports/templates/${importType}`, {
      headers,
    });
    if (!response.ok) {
      return;
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${importType.toLowerCase()}_template.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  /**
   * Resets the import state back to idle and stops any active polling.
   */
  const reset = () => {
    stopPolling();
    setStage("idle");
    setSummary(null);
    setErrorMessage(null);
  };

  return { stage, summary, errorMessage, startImport, downloadTemplate, reset };
}
